import { useEffect, useMemo, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { api } from '@/lib/api'
import { useDailyReviewStore } from '@/stores/dailyReview'
import { AdoptionTable } from '@/components/daily-review/AdoptionTable'
import { ShootCard } from '@/components/daily-review/ShootCard'
import { DownloadMenu } from '@/components/daily-review/DownloadMenu'
import { countryLabel, formatBytes } from '@/components/daily-review/constants'
import type { ReviewShootDetail } from '@/types'

type VerdictFilter = 'all' | 'pass' | 'fail'

/** 单个市场的预加载控件：按钮 → 排队/进度/取消 → 已缓存。自订阅 store，进度高频更新只重渲本控件。 */
function CountryPreloadControl({
  country,
  shoots,
}: {
  country: string
  shoots: ReviewShootDetail[]
}) {
  const progress = useDailyReviewStore((s) => s.preloads[country])
  const videoBlobs = useDailyReviewStore((s) => s.videoBlobs)
  const startPreload = useDailyReviewStore((s) => s.startPreload)
  const cancelPreload = useDailyReviewStore((s) => s.cancelPreload)

  const assets = useMemo(
    () =>
      shoots.flatMap((shoot) =>
        shoot.assets
          .filter(
            (a) => a.asset_type === 'video' && a.role === 'canonical_variant' && a.preview_url,
          )
          .map((a) => ({
            id: a.id,
            // 走同源后端代理而非 S3 预签名 URL：fetch() 受 CORS 限制，代理不受
            url: api.buildDailyBatchAssetRawUrl(a.id),
            size: a.file_size,
            mime: a.mime_type,
          })),
      ),
    [shoots],
  )
  const uncached = useMemo(() => assets.filter((a) => !videoBlobs[a.id]), [assets, videoBlobs])
  const uncachedBytes = uncached.reduce((acc, a) => acc + (a.size ?? 0), 0)

  if (progress?.status === 'queued') {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-gray-500">
        <Loader2 className="h-3 w-3 animate-spin" />
        排队等待预加载…
        <button
          type="button"
          className="text-blue-600 hover:underline"
          onClick={() => cancelPreload(country)}
        >
          取消
        </button>
      </span>
    )
  }
  if (progress?.status === 'running') {
    return (
      <span className="inline-flex items-center gap-2 text-xs text-gray-600">
        <Loader2 className="h-3 w-3 animate-spin" />
        预加载中 {progress.done + progress.failed}/{progress.total} ·{' '}
        {formatBytes(progress.bytesDone) || '0 KB'}
        {progress.bytesTotal > 0 ? ` / ~${formatBytes(progress.bytesTotal)}` : ''}
        {progress.failed > 0 ? ` · 失败 ${progress.failed}` : ''}
        <button
          type="button"
          className="text-blue-600 hover:underline"
          onClick={() => cancelPreload(country)}
        >
          取消
        </button>
      </span>
    )
  }
  if (assets.length === 0) return null
  if (uncached.length === 0) {
    return <span className="text-xs text-emerald-600">✓ 已缓存（{assets.length} 个视频）</span>
  }
  return (
    <button
      type="button"
      className="text-xs text-blue-600 hover:underline"
      onClick={() => startPreload(country, assets)}
    >
      ⬇ 预加载（{uncached.length} 个 · ~{formatBytes(uncachedBytes)}）
      {progress?.status === 'done' && progress.failed > 0 ? ` · 上次失败 ${progress.failed}` : ''}
    </button>
  )
}

function shootHasVerdict(shoot: ReviewShootDetail, verdict: VerdictFilter): boolean {
  if (verdict === 'all') return true
  const results = Object.values(shoot.variant_results ?? {})
  return results.some((r) => r?.result === (verdict === 'pass' ? 'PASS' : 'FAIL'))
}

function Chip({
  active,
  loading,
  onClick,
  children,
}: {
  active: boolean
  loading?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium border transition-colors ${
        active
          ? 'bg-blue-600 text-white border-blue-600'
          : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
      }`}
    >
      {loading && <Loader2 className="h-3 w-3 animate-spin" />}
      {children}
    </button>
  )
}

export function DailyReviewBatchPage() {
  const { batchId } = useParams<{ batchId: string }>()
  const {
    batch,
    adoption,
    countryShoots,
    loadingCountries,
    error,
    initBatch,
    loadCountry,
    startReviewsPolling,
    stopReviewsPolling,
    resetBatch,
    preloads,
  } = useDailyReviewStore()

  const [countries, setCountries] = useState<string[]>([])
  const [selected, setSelected] = useState<string>('')
  const [verdictFilter, setVerdictFilter] = useState<VerdictFilter>('all')

  useEffect(() => {
    if (!batchId) return
    let cancelled = false
    ;(async () => {
      const countryList = await initBatch(batchId)
      if (cancelled || countryList.length === 0) return
      setCountries(countryList)
      // Load only the first product — keeps the initial page light
      setSelected(countryList[0])
      loadCountry(batchId, countryList[0])
    })()
    startReviewsPolling(batchId)
    return () => {
      cancelled = true
      stopReviewsPolling()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [batchId])

  useEffect(() => () => resetBatch(), [resetBatch])

  // useMemo：预加载进度高频更新期间避免反复重算可见列表
  const visibleCountries = useMemo(
    () =>
      (selected === 'all' ? countries : [selected])
        .filter((country) => countryShoots[country])
        .map((country) => ({
          country,
          shoots: countryShoots[country].filter((s) => shootHasVerdict(s, verdictFilter)),
        }))
        .filter((group) => group.shoots.length > 0),
    [selected, countries, countryShoots, verdictFilter],
  )

  if (error) {
    return <div className="p-6 text-sm text-red-600">{error}</div>
  }

  if (!batch || batch.id !== batchId) {
    return (
      <div className="p-6 flex items-center gap-2 text-gray-500 text-sm">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading batch…
      </div>
    )
  }

  const selectCountry = (country: string) => {
    setSelected(country)
    if (country === 'all') {
      // Load remaining products one by one; they render as they arrive
      for (const c of countries) loadCountry(batch.id, c)
    } else {
      loadCountry(batch.id, country)
    }
  }

  const visibleAdoption =
    selected === 'all' ? adoption : adoption.filter((row) => row.country === selected)

  const shootCounts = batch.stats.countries ?? {}

  return (
    <div className="p-6 max-w-[1600px] mx-auto space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/internal/daily-review" className="text-gray-400 hover:text-gray-600">
          <ArrowLeft className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-xl font-bold">{batch.name}</h1>
          <p className="text-xs text-gray-500">
            {batch.batch_date ?? ''} · {batch.stats.shoot_count ?? 0} shoots ·{' '}
            {batch.stats.video_count ?? 0} videos · PASS {batch.stats.pass ?? 0} / FAIL{' '}
            {batch.stats.fail ?? 0}
          </p>
        </div>
        <div className="ml-auto">
          <DownloadMenu batchId={batch.id} countries={countries} />
        </div>
      </div>

      {/* Product chips — each product loads on demand */}
      <div className="sticky top-0 z-20 bg-gray-50/95 backdrop-blur py-2">
        <div className="flex flex-wrap gap-2 items-center">
          {countries.map((country) => {
            const preloadStatus = preloads[country]?.status
            return (
              <Chip
                key={country}
                active={selected === country}
                loading={loadingCountries.includes(country)}
                onClick={() => selectCountry(country)}
              >
                {countryLabel(country)}{' '}
                <span className="opacity-60">({shootCounts[country] ?? '…'})</span>
                {(preloadStatus === 'running' || preloadStatus === 'queued') && (
                  <Loader2 className="h-3 w-3 animate-spin" />
                )}
                {preloadStatus === 'done' && <span className="text-emerald-500">✓</span>}
              </Chip>
            )
          })}
          <Chip active={selected === 'all'} onClick={() => selectCountry('all')}>
            All products
          </Chip>
          <span className="mx-1 h-4 w-px bg-gray-300" />
          {(['all', 'pass', 'fail'] as const).map((verdict) => (
            <Chip
              key={verdict}
              active={verdictFilter === verdict}
              onClick={() => setVerdictFilter(verdict)}
            >
              {verdict === 'all' ? 'All' : verdict === 'pass' ? 'Has PASS' : 'Has FAIL'}
            </Chip>
          ))}

        </div>
      </div>

      <AdoptionTable rows={visibleAdoption} />

      {visibleCountries.length === 0 && (
        <div className="rounded-lg border border-dashed border-gray-300 p-8 text-center text-sm text-gray-400">
          {loadingCountries.length > 0 ? (
            <span className="inline-flex items-center gap-2">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading {loadingCountries.join(', ')}…
            </span>
          ) : (
            'No shoots match the current filters.'
          )}
        </div>
      )}

      {visibleCountries.map((group) => (
        <section key={group.country} className="space-y-3">
          <h2 className="flex items-center gap-3 text-base font-semibold py-1">
            {countryLabel(group.country)}{' '}
            <span className="text-xs font-normal text-gray-500">({group.shoots.length} shoots)</span>
            <span className="ml-auto font-normal">
              <CountryPreloadControl country={group.country} shoots={group.shoots} />
            </span>
          </h2>
          {/* Two shoot cards per row — 4 variant videos visible at once */}
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 items-start">
            {group.shoots.map((shoot) => (
              <ShootCard key={shoot.id} shoot={shoot} batchId={batch.id} />
            ))}
          </div>
        </section>
      ))}
    </div>
  )
}
